import React from "react";
// import { useNavigate } from "react-router-dom";
import {
  Users,
  Target,
  CalendarCheck,
  UserRound,
  BarChart3,
  Settings,
  FilePlus2,
  Handshake,
  ArrowRight,
  CheckCircle2,

} from "lucide-react";
import { ChartNoAxesColumnIncreasing } from "lucide-react";
import "./LeadManagementPage.css";
import dashboardImage from "../assets/iconiq-crm-dashboard.png";
import whyChooseImage from "../assets/iconiq-crm-why-choose.png";

const features = [
  {
    icon: Users,
    title: "Lead Management",
    text: "Capture and track leads from multiple sources, organize and prioritize effectively.",
    color: "blue",
  },
  {
    icon: Target,
    title: "Lead Tracking",
    text: "Follow the complete sales journey and monitor lead status in real time.",
    color: "pink",
  },
  {
    icon: CalendarCheck,
    title: "Follow-up Management",
    text: "Schedule follow-ups, set reminders and never miss an opportunity.",
    color: "purple",
  },
  {
    icon: UserRound,
    title: "Customer Management",
    text: "Maintain customer details, interaction history and build stronger relationships.",
    color: "sky",
  },
  {
    icon: BarChart3,
    title: "Reports & Insights",
    text: "Get clear reports and valuable insights on lead performance and trends.",
    color: "green",
  },
  {
    icon: Settings,
    title: "Sales Workflow",
    text: "Automate and streamline sales activities with a structured workflow.",
    color: "orange",
  },
];

const workflow = [
  {
    icon: FilePlus2,
    title: "Capture Leads",
    text: "From website, campaigns or manual entry.",
    color: "violet",
  },
  {
    icon: Users,
    title: "Assign & Track",
    text: "Assign to team and track progress.",
    color: "blue",
  },
  {
    icon: CalendarCheck,
    title: "Follow-up",
    text: "Schedule and manage follow-ups.",
    color: "green",
  },
  {
    icon: BarChart3,
    title: "Convert",
    text: "Turn leads into customers.",
    color: "orange",
  },
  {
    icon: Handshake,
    title: "Build Relationships",
    text: "Maintain long-term customer relationships.",
    color: "red",
  },
];

const LeadManagementPage = () => {
//   const navigate = useNavigate();

  const openIconiq = () => {
    window.open("https://iconiqcrm.com/", "_blank", "noopener,noreferrer");
  };

  return (
    <main className="lmp-page">
      
{/* HERO SECTION */}
<section className="lmp-hero">
  <div className="lmp-hero-inner">
    <div className="lmp-hero-copy">
      <div className="lmp-eyebrow">
  <div className="lmp-eyebrow-icon">
    <ChartNoAxesColumnIncreasing
      size={21}
      strokeWidth={4}
    />
  </div>

  <span>Powering Better Customer Relationships</span>
</div>

      <div className="lmp-brand">
        <span>
          ICONiQ <b>CRM</b>
        </span>
      </div>

      <h1>
        Turn Every Lead Into a
        <span>Growth Opportunity</span>
      </h1>

      <p className="lmp-hero-description">
        Capture, track and convert leads efficiently with a centralized
        platform. Manage customer relationships, follow-ups and sales
        activities — all in one place.
      </p>

      <div className="lmp-actions">
        <button
          className="lmp-button lmp-button-primary"
          onClick={openIconiq}
        >
          Explore ICONiQ CRM
          <ArrowRight size={20} />
        </button>
      </div>

      <div className="lmp-benefits">
        <span>
          <CheckCircle2 size={20} />
          Better lead tracking
        </span>
        <span>
          <CheckCircle2 size={20} />
          Higher conversion rates
        </span>
        <span>
          <CheckCircle2 size={20} />
          Improved team productivity
        </span>
      </div>
    </div>

    <div className="lmp-dashboard-image">
      <img
        src={dashboardImage}
        alt="iCONiQ CRM dashboard showing lead analytics and sales performance"
      />
    </div>
  </div>
</section>


      {/* KEY FEATURES */}
      <section className="lmp-features section-wrap">
        <div className="lmp-section-label">KEY FEATURES</div>

        <h2>
          Everything You Need to <span>Manage Leads</span>
        </h2>

        <div className="lmp-feature-grid">
          {features.map(({ icon: Icon, title, text, color }) => (
            <article className="lmp-feature-card" key={title}>
              <div className={`lmp-feature-icon ${color}`}>
                <Icon size={27} strokeWidth={2.4} />
              </div>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="lmp-workflow">
        <div className="lmp-section-label">HOW IT WORKS</div>
        <h2>
          From Lead to <span>Customer</span>
        </h2>

        <div className="lmp-workflow-inner">
          {workflow.map(({ icon: Icon, title, text, color }, index) => (
            <React.Fragment key={title}>
              <div className="lmp-work-step">
                <div className={`lmp-work-icon ${color}`}>
                  <Icon size={29} />
                </div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>

              {index < workflow.length - 1 && (
                <ArrowRight className="lmp-work-arrow" size={23} />
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE ICONIQ CRM */}
      <section className="lmp-why section-wrap">
        <div className="lmp-why-copy">
          <div className="lmp-section-label lmp-left-label">
            WHY CHOOSE ICONIQ CRM
          </div>

          <h2>Built for Higher Conversions</h2>

          <p>
            ICONiQ CRM helps you streamline your sales process, stay organized
            and build lasting customer relationships with the right tools and
            insights.
          </p>

          <ul>
            <li>
              <CheckCircle2 size={19} /> Easy to use and intuitive interface
            </li>
            <li>
              <CheckCircle2 size={19} /> Customizable to fit your business needs
            </li>
            <li>
              <CheckCircle2 size={19} /> Secure and reliable cloud platform
            </li>
            <li>
              <CheckCircle2 size={19} /> Dedicated support and training
            </li>
          </ul>
        </div>

        {/* <div className="lmp-sales-preview">
          <div className="lmp-contact-preview">
            <div className="lmp-avatar">JM</div>

            <div className="lmp-contact-name">
              <b>John Mathew</b>
              <span className="lmp-qualified">Qualified</span>
            </div>

            <p>
              <Phone size={14} /> +91 98765 43210
            </p>
            <p>
              <Mail size={14} /> john@company.com
            </p>
            <p>
              <MapPin size={14} /> Bangalore
            </p>

            <div className="lmp-lead-source">
              Lead Source <b>Website</b>
            </div>

            <div className="lmp-next-follow">
              Next Follow-up <b>25 Apr 2025</b>
            </div>
          </div>

          <div className="lmp-pipeline-preview">
            <h3>Sales Pipeline</h3>

            {[
              ["New", "52", "p-blue"],
              ["Contacted", "38", "p-cyan"],
              ["Qualified", "28", "p-green"],
              ["Proposal", "16", "p-orange"],
              ["Converted", "12", "p-purple"],
            ].map(([label, value, cls]) => (
              <div className="lmp-pipeline-row" key={label}>
                <span className={cls}>{label}</span>
                <b>{value}</b>
              </div>
            ))}
          </div>
        </div> */}

<div className="lmp-sales-preview lmp-why-image">
  <img
    src={whyChooseImage}
    alt="ICONIQ CRM customer profile, sales pipeline and lead analytics dashboard"
  />
</div>

      </section>

      {/* CALL TO ACTION */}
      <section className="lmp-cta">
        <div>
          <h2>
            Ready to Grow Your Business
            <br />
            with <span>ICONiQ CRM?</span>
          </h2>

          <p>
            Get started and experience a smarter way to manage your leads,
            <br className="lmp-desktop-break" />
            customers and sales process.
          </p>
        </div>

        <div className="lmp-cta-actions">
          <button
            className="lmp-button lmp-button-primary"
            onClick={openIconiq}
          >
            Explore IconiqCRM <ArrowRight size={18} />
          </button>

          {/* <button
            className="lmp-button lmp-button-outline"
            onClick={() => navigate("/contactus")}
          >
            Contact Us
          </button> */}
        </div>
      </section>
    </main>
  );
};

export default LeadManagementPage;
